import { z } from "zod";
import type { SiteCreateRequest, SitePullResource } from "@carnet/api/backoffice";
import { formatApiCoord } from "./geo";
import { safeExternalHttpUrl } from "./externalUrl";

// Logique partagée du tunnel de création de site (étapes web ↔ modale mobile).
// Forme du formulaire, schémas de validation par étape et construction du payload SDK
// sont mutualisés ; seules diffèrent la coquille (pages/modale web vs modale Ionic) et la
// sélection de la position (carte interactive des deux côtés, recherche Mapbox de la ville).

export interface SiteCreateForm {
  name: string;
  // Nom de la ville la plus proche, saisi librement (`SiteCreateRequest.nearbyCityName`).
  nearbyCityName: string;
  zoneCode: string;
  activityTypes: string[];
  situation: string;
  environment: string;
  depthCategory: string;
  condition: string;
  maxDepth: number | null;
  // Point précis du site choisi sur la carte (étape position).
  latitude: number;
  longitude: number;
  positionSet: boolean;
  // Relevée sur le relief Mapbox à la validation de la position ; null si le relevé a échoué.
  altitude: number | null;
  description: string;
  services: string;
  hasRegulatedAccess: boolean;
  regulatedAccess: string;
  regulatedAccessLink: string;
  facilities: string;
  interests: string;
  security: string;
  moreInformations: string;
  externalUrl: string;
  habitats: string[];
  localFeatures: string[];
  // uuid v7 stable rattachant les photos uploadées au site avant sa création.
  clientUuid: string;
}

// Centre par défaut de la carte (France métropolitaine) : [lng, lat].
export const SITE_CREATE_INITIAL_CENTER: [number, number] = [2.5, 47.5];

// Vue de départ de la carte du tunnel de création : reprise du cadrage courant de la carte
// appelante (étape site d'une création d'activité, page Sites…) pour ne pas repartir d'une carte
// dézoomée sur le monde entier. `center` = [lng, lat], `zoom` = niveau Mapbox.
export interface SiteCreateInitialView {
  center: [number, number];
  zoom: number;
}

// Garde-fou : les liens « Créer un site » sont parfois branchés en `@click="open"`, qui passe alors
// l'événement souris en argument. On ne retient que les vues valides (sinon → cadrage par défaut).
export function asSiteCreateInitialView(value: unknown): SiteCreateInitialView | null {
  if (!value || typeof value !== "object") return null;
  const { center, zoom } = value as { center?: unknown; zoom?: unknown };
  if (!Array.isArray(center) || center.length !== 2) return null;
  const [lng, lat] = center;
  if (typeof lng !== "number" || typeof lat !== "number" || typeof zoom !== "number") return null;
  if (!Number.isFinite(lng) || !Number.isFinite(lat) || !Number.isFinite(zoom)) return null;
  return { center: [lng, lat], zoom };
}

export const createSiteCreateForm = (): SiteCreateForm => ({
  name: "",
  nearbyCityName: "",
  zoneCode: "",
  activityTypes: [],
  situation: "",
  environment: "",
  depthCategory: "",
  condition: "",
  maxDepth: null,
  latitude: SITE_CREATE_INITIAL_CENTER[1],
  longitude: SITE_CREATE_INITIAL_CENTER[0],
  positionSet: false,
  altitude: null,
  description: "",
  services: "",
  hasRegulatedAccess: false,
  regulatedAccess: "",
  regulatedAccessLink: "",
  facilities: "",
  interests: "",
  security: "",
  moreInformations: "",
  externalUrl: "",
  habitats: [],
  localFeatures: [],
  clientUuid: "",
});

// `t` minimal : suffisant pour les messages de validation (clé + interpolation éventuelle).
type TranslateFn = (key: string, named?: Record<string, unknown>) => string;

// Identité : nom du site + nom de la ville la plus proche (champ texte libre requis).
export const siteIdentityStepSchema = (t: TranslateFn) =>
  z.object({
    name: z.string({ message: t("form.validation.required") }).trim().min(1, t("form.validation.required")),
    city: z.string({ message: t("form.validation.required") }).trim().min(1, t("form.validation.required")),
  });

export const siteCharacteristicsStepSchema = (t: TranslateFn) =>
  z.object({
    zoneCode: z.string().min(1, t("form.validation.select_option")),
    activityTypes: z.array(z.string()).min(1, t("form.validation.select_option")),
    situation: z.string().min(1, t("form.validation.select_option")),
    environment: z.string().min(1, t("form.validation.select_option")),
    depthCategory: z.string().min(1, t("form.validation.select_option")),
    condition: z.string().min(1, t("form.validation.select_option")),
    maxDepth: z
      .number({ message: t("form.validation.maxDepth_required") })
      .min(0, t("form.validation.maxDepth_range"))
      .max(10984, t("form.validation.maxDepth_range"))
      .multipleOf(0.1, t("form.validation.maxDepth_decimals")),
  });

const optionalHttpUrl = (t: TranslateFn) =>
  z
    .string()
    .trim()
    .refine((value) => !value || Boolean(safeExternalHttpUrl(value)), t("form.validation.url_format"))
    .optional();

// Les champs sans règle figurent quand même dans le schéma : les erreurs renvoyées par l'API y
// retrouvent leur `UFormField` (cf. `fields: Object.keys(schema.shape)` côté appelants).
export const siteAdditionalInfoSchema = (t: TranslateFn) =>
  z.object({
    description: z.string().optional(),
    services: z.string().optional(),
    hasRegulatedAccess: z.boolean().optional(),
    regulatedAccess: z.string().optional(),
    regulatedAccessLink: optionalHttpUrl(t),
    facilities: z.string().optional(),
    interests: z.string().optional(),
    security: z.string().optional(),
    moreInformations: z.string().optional(),
    externalUrl: optionalHttpUrl(t),
    habitats: z.array(z.string()).optional(),
    localFeatures: z.array(z.string()).optional(),
  });

// Schéma complet (toutes les étapes de saisie) : utilisé par la coquille web qui valide le
// formulaire d'un bloc, et comme garde-fou à la soumission mobile.
export const siteCreateSchema = (t: TranslateFn) =>
  z.object({
    ...siteIdentityStepSchema(t).shape,
    ...siteCharacteristicsStepSchema(t).shape,
    ...siteAdditionalInfoSchema(t).shape,
  });

const trimmedOrNull = (value: string): string | null => value.trim() || null;

// Construit le corps POST `sites/create` à partir du formulaire. `latitude`/`longitude` = point
// précis (carte) ; `nearbyCityName` = nom de la ville la plus proche (texte libre). Le garde-fou
// évite un payload incohérent si la fonction est appelée sans ville saisie.
export function buildSiteCreateBody(form: SiteCreateForm): SiteCreateRequest {
  const nearbyCityName = form.nearbyCityName.trim();
  if (!nearbyCityName) {
    throw new Error("buildSiteCreateBody: nearbyCityName manquant (ville la plus proche non saisie)");
  }
  return {
    name: form.name,
    zoneCode: form.zoneCode,
    situation: form.situation,
    environment: form.environment,
    depthCategory: form.depthCategory,
    maxDepth: form.maxDepth ?? 0,
    condition: form.condition,
    activityTypes: form.activityTypes,
    clientUuid: form.clientUuid,
    latitude: formatApiCoord(form.latitude),
    longitude: formatApiCoord(form.longitude),
    nearbyCityName,
    altitude: form.altitude,
    description: trimmedOrNull(form.description),
    services: trimmedOrNull(form.services),
    hasRegulatedAccess: form.hasRegulatedAccess,
    regulatedAccess: form.hasRegulatedAccess ? trimmedOrNull(form.regulatedAccess) : null,
    regulatedAccessLink: form.hasRegulatedAccess ? safeExternalHttpUrl(form.regulatedAccessLink) ?? null : null,
    facilities: trimmedOrNull(form.facilities),
    hasFacilities: Boolean(form.facilities.trim()),
    interests: trimmedOrNull(form.interests),
    security: trimmedOrNull(form.security),
    moreInformations: trimmedOrNull(form.moreInformations),
    externalUrl: safeExternalHttpUrl(form.externalUrl) ?? null,
    habitats: form.habitats,
    localFeatures: form.localFeatures,
  };
}

// Valeurs validées du formulaire complet (sortie du schéma) : partagé par la coquille web
// (typage de l'événement de soumission `FormSubmitEvent`) et la modale mobile.
export type SiteCreateFormValues = z.output<ReturnType<typeof siteCreateSchema>>;

type CodeLabel = { code: string; label: string };

export interface SiteCreateReferentials {
  zones?: ReadonlyArray<CodeLabel>;
  activityTypes?: ReadonlyArray<CodeLabel>;
  siteSituations?: ReadonlyArray<CodeLabel>;
  environments?: ReadonlyArray<CodeLabel>;
  siteDepthCategories?: ReadonlyArray<CodeLabel>;
  siteConditions?: ReadonlyArray<CodeLabel>;
  habitats?: ReadonlyArray<CodeLabel>;
  localFeatures?: ReadonlyArray<CodeLabel>;
}

export function buildPendingSitePullResource(form: SiteCreateForm, referentials: SiteCreateReferentials): Omit<SitePullResource, "id"> {
  const labeled = (list: ReadonlyArray<CodeLabel> | undefined, code: string): CodeLabel => ({
    code,
    label: list?.find((item) => item.code === code)?.label ?? code,
  });
  return {
    clientUuid: form.clientUuid || null,
    name: form.name,
    searchableName: form.name,
    validationStatus: "waiting_for_validation",
    zone: labeled(referentials.zones, form.zoneCode),
    latitude: form.latitude,
    longitude: form.longitude,
    nearbyCityLabel: form.nearbyCityName.trim(),
    activityTypes: form.activityTypes.map((code) => labeled(referentials.activityTypes, code)),
    depthCategory: labeled(referentials.siteDepthCategories, form.depthCategory),
    environment: labeled(referentials.environments, form.environment),
    situation: labeled(referentials.siteSituations, form.situation),
    condition: labeled(referentials.siteConditions, form.condition),
    maxDepth: form.maxDepth ?? 0,
    altitude: form.altitude,
    description: form.description.trim(),
    services: form.services.trim(),
    regulated_access: form.hasRegulatedAccess ? form.regulatedAccess.trim() : "",
    regulated_access_link: form.hasRegulatedAccess ? safeExternalHttpUrl(form.regulatedAccessLink) ?? "" : "",
    facilities: form.facilities.trim(),
    interests: form.interests.trim(),
    security: form.security.trim(),
    more_informations: form.moreInformations.trim(),
    external_url: safeExternalHttpUrl(form.externalUrl) ?? "",
    habitats: form.habitats.map((code) => labeled(referentials.habitats, code)),
    localFeatures: form.localFeatures.map((code) => labeled(referentials.localFeatures, code)),
    images: [],
    updatedAt: new Date().toISOString(),
    deletedAt: null,
  };
}
